import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router';
import { useForm } from 'antd/es/form/Form';
import ProductService from 'services/product';
import ProductOptionService from 'services/product_option';
import ProductVariantService from 'services/product_variant';
import { useApp } from 'contexts/app';

const service = new ProductService();
const productOptionService = new ProductOptionService();
const productVariantService = new ProductVariantService();
const DEFAULT_OPTION = { id: 'default', title: 'Mặc định', values: ['default'] };

const getVariantImage = (variant) => variant?.image || variant?.images?.[0] || null;
const normalizeVariantImage = (variant) => {
  const image = getVariantImage(variant);
  const nextVariant = { ...variant, image };
  delete nextVariant.images;
  return nextVariant;
};

const cloneOptions = (items = []) => (items || []).map((o) => ({
  ...o,
  values: [...(o?.values || [])],
  option_values: [...(o?.option_values || [])],
}));

const cloneVariants = (items = []) => (items || []).map((v) => ({
  ...v,
  options: [...(v?.options || [])],
  price_list: [...(v?.price_list || [])],
}));

const buildDefaultVariant = (seed = {}) => ({
  ...seed,
  variant_id: 'default',
  variant_title: 'Mặc định',
  options: [{ id: 'default', value: 'default' }],
  image: seed?.image || null,
  original_price: Number(seed?.original_price ?? 0) || 0,
  price_list: [...(seed?.price_list || [])],
});

const hasAnyNonDefaultOption = (opts = []) => (opts || []).some((x) => x?.id !== 'default');

const useComponent = (config = {}) => {
  const { id: routeId } = useParams();
  const id = config?.id ?? routeId;
  const draftMode = !!config?.draftMode;
  const [form] = useForm();
  const navigate = useNavigate();
  const { showNotify, showLoading } = useApp();

  const [options, setOptions] = useState(cloneOptions(config?.draftOptions?.length ? config.draftOptions : [DEFAULT_OPTION]));
  const [product, setProduct] = useState({});
  const [productOptions, setProductOptions] = useState([]);
  const [productVariants, setProductVariants] = useState(cloneVariants(config?.draftVariants || []));
  const [savedVariant, setSavedvariant] = useState(cloneVariants(config?.draftVariants || []));
  const [hasMultipleVariants, setHasMultipleVariants] = useState(hasAnyNonDefaultOption(config?.draftOptions || []));
  const [variantChangeByUser, setVariantChangeByUser] = useState(false);

  const emitVariantState = (nextOptions, nextVariants) => {
    if (draftMode) {
      config?.onDraftChange?.({ options: nextOptions, variants: nextVariants });
      return;
    }
    config?.onStateChange?.({ options: nextOptions, variants: nextVariants });
  };

  const toSingleVariantState = (seedVariants = []) => {
    const seed = (seedVariants || [])[0] || {};
    return {
      options: cloneOptions([DEFAULT_OPTION]),
      variants: [buildDefaultVariant(seed)],
    };
  };

  useEffect(() => {
    loadProductOptions();
  }, []);

  useEffect(() => {
    if (!draftMode && id) {
      loadData();
    }
  }, [id, draftMode]);

  useEffect(() => {
    if (!draftMode) {
      return;
    }

    setVariantChangeByUser(false);

    const draftOptions = cloneOptions(config?.draftOptions?.length ? config.draftOptions : [DEFAULT_OPTION]);
    const draftVariants = cloneVariants(config?.draftVariants || []);
    const nextHasMultiple = hasAnyNonDefaultOption(draftOptions);
    setHasMultipleVariants(nextHasMultiple);

    if (!nextHasMultiple) {
      const singleState = toSingleVariantState(draftVariants);
      setOptions(singleState.options);
      setProductVariants(singleState.variants);
      setSavedvariant(singleState.variants);
      return;
    }

    setOptions(draftOptions);
    setProductVariants(draftVariants);
    setSavedvariant(draftVariants);
  }, [draftMode, config?.draftOptions, config?.draftVariants]);

  useEffect(() => {
    if (variantChangeByUser) {
      createProductVariants();
    }
  }, [options, variantChangeByUser]);

  const onFinish = async (values) => {
    if (id) {
      await service.updateGeneralInfo(values);
      navigate(`/product/edit/${id}`);
    } else {
      const created = await service.create(values);
      navigate(`/product/edit/${created.id}`);
    }
  };

  const onFinishFailed = () => {};

  const loadData = async () => {
    setProduct({});
    const item = await service.get(id);
    setProduct(item || {});
    form.setFieldsValue(item);
    setVariantChangeByUser(false);
    const normalizedVariants = (item?.variants || []).map(normalizeVariantImage);
    const loadedOptions = cloneOptions(item?.options || []);
    const nextHasMultiple = hasAnyNonDefaultOption(loadedOptions);
    setHasMultipleVariants(nextHasMultiple);

    if (!nextHasMultiple) {
      const singleState = toSingleVariantState(normalizedVariants);
      setOptions(singleState.options);
      setProductVariants(singleState.variants);
      setSavedvariant(singleState.variants);
      config?.onStateChange?.({ options: singleState.options, variants: singleState.variants });
      return;
    }

    setOptions(loadedOptions);
    setProductVariants(normalizedVariants);
    setSavedvariant(normalizedVariants);
    config?.onStateChange?.({ options: loadedOptions, variants: normalizedVariants });
  };

  const loadProductOptions = async () => {
    const lst = await productOptionService.list();
    setProductOptions(lst || []);
  };

  const createProductVariants = () => {
    if (!hasMultipleVariants) {
      const singleState = toSingleVariantState(productVariants?.length ? productVariants : savedVariant);
      setOptions(singleState.options);
      setProductVariants(singleState.variants);
      setVariantChangeByUser(false);
      emitVariantState(singleState.options, singleState.variants);
      return;
    }

    if (!options.length) {
      setProductVariants([]);
      setVariantChangeByUser(false);
      emitVariantState(options, []);
      return;
    }

    let lst = [];
    const optionValues0 = options?.[0]?.values;
    const optionValues1 = options?.[1]?.values;

    if (!optionValues0?.length) {
      setProductVariants([]);
      setVariantChangeByUser(false);
      emitVariantState(options, []);
      return;
    }

    const option0 = productOptions.find((x) => x.id == options[0].id);
    optionValues0.forEach((o) => {
      const option0Value = option0?.values?.find((x) => x.id == o) || { id: o, title: o };

      const variant = {
        variant_id: option0Value.id,
        variant_title: option0Value.title,
        options: [{ id: options[0].id, value: o }],
        image: null,
        price: 0,
      };
      const variantOld = savedVariant.find((x) => x.variant_id == variant.variant_id);
      if (variantOld) {
        lst.push(normalizeVariantImage(variantOld));
      } else {
        lst.push(variant);
      }
    });

    if (optionValues1?.length) {
      const option1 = productOptions.find((x) => x.id == options[1].id);
      const lst2 = [...lst];
      lst = [];

      optionValues1.forEach((o) => {
        const option1Value = option1?.values?.find((x) => x.id == o) || { id: o, title: o };

        lst2.forEach((element) => {
          const variant = {
            variant_id: `${element.variant_id}_${option1Value.id}`,
            variant_title: `${element.variant_title} ${option1Value.title}`,
            options: [...element.options, { id: options[1].id, value: o }],
            image: null,
            price: 0,
          };
          const variantOld = savedVariant.find((x) => x.variant_id == variant.variant_id);
          if (variantOld) {
            lst.push(normalizeVariantImage(variantOld));
          } else {
            lst.push(variant);
          }
        });
      });
    }

    setProductVariants(lst);
    setVariantChangeByUser(false);
    emitVariantState(options, lst);
  };

  const onMultipleVariantToggle = (enabled) => {
    const nextEnabled = !!enabled;
    setHasMultipleVariants(nextEnabled);
    setVariantChangeByUser(false);

    if (!nextEnabled) {
      const singleState = toSingleVariantState(productVariants?.length ? productVariants : savedVariant);
      setOptions(singleState.options);
      setProductVariants(singleState.variants);
      setSavedvariant(singleState.variants);
      emitVariantState(singleState.options, singleState.variants);
      return;
    }

    const nextOptions = options.filter((x) => x.id !== 'default');
    setOptions(nextOptions);
    setProductVariants([]);
    emitVariantState(nextOptions, []);
  };

  const onSelectOptionChange = (optionId, values) => {
    const newOptions = [...options];
    const index = newOptions.findIndex((x) => x.id == optionId);
    if (index === -1) {
      return;
    }

    newOptions[index].values = values;
    setOptions(newOptions);
    setVariantChangeByUser(true);
  };

  const applyAttributeSelections = (rows = []) => {
    const cleanRows = (rows || []).filter((x) => x?.id);

    if (!cleanRows.length) {
      setHasMultipleVariants(false);
      const singleState = toSingleVariantState(productVariants?.length ? productVariants : savedVariant);
      setOptions(singleState.options);
      setProductVariants(singleState.variants);
      setSavedvariant(singleState.variants);
      setVariantChangeByUser(false);
      emitVariantState(singleState.options, singleState.variants);
      return;
    }

    const nextOptions = cleanRows.map((row) => {
      const found = productOptions.find((o) => String(o.id) === String(row.id));
      return {
        ...(found || { id: row.id, title: row.id }),
        option_values: found?.values || [],
        values: [...(row?.values || [])],
      };
    });

    setHasMultipleVariants(true);
    setOptions(nextOptions);
    setVariantChangeByUser(true);
    emitVariantState(nextOptions, productVariants);
  };

  const onUpdateProductVariant = async () => {
    if (draftMode) {
      emitVariantState(options, productVariants);
      showNotify('Đã cập nhật biến thể tạm thời');
      return;
    }

    showLoading(true);
    try {
      await productVariantService.update({ id, options, variants: productVariants });
      showNotify('Đã cập nhật');
    } finally {
      showLoading(false);
    }
  };

  const onVariantImageChange = (image, variantId) => {
    const i = productVariants.findIndex((x) => x.variant_id == variantId);
    if (i === -1) {
      return;
    }

    const newVariants = [...productVariants];
    newVariants[i].image = image;
    setProductVariants(newVariants);
    emitVariantState(options, newVariants);
  };

  const onGroupImageChange = (image, opt0Id, optionValue) => {
    setProductVariants((prev) => {
      const next = prev.map((v) => {
        const match = v.options?.find((o) => o.id === opt0Id)?.value === optionValue;
        return match ? { ...v, image } : v;
      });
      emitVariantState(options, next);
      return next;
    });
  };

  const onVariantOriginalPriceChange = (price, variantId) => {
    const i = productVariants.findIndex((x) => x.variant_id == variantId);
    if (i === -1) {
      return;
    }

    const newVariants = [...productVariants];
    newVariants[i].original_price = price;
    setProductVariants(newVariants);
    emitVariantState(options, newVariants);
  };

  const attributeChange = (values) => {
    if (!values?.length) {
      setHasMultipleVariants(false);
      const singleState = toSingleVariantState(productVariants?.length ? productVariants : savedVariant);
      setOptions(singleState.options);
      setProductVariants(singleState.variants);
      emitVariantState(singleState.options, singleState.variants);
      return;
    }

    const lstNew = values.map((x) => {
      let option = options.find((o) => o.id == x);
      if (!option) {
        option = productOptions.find((o) => o.id == x);
        option = { ...option, option_values: option.values, values: [] };
      }
      return option;
    });

    setHasMultipleVariants(true);
    setOptions([...lstNew]);
    setVariantChangeByUser(true);
    emitVariantState([...lstNew], productVariants);
  };

  const removeOption = (optionId) => {
    let lstNew = [...options];
    const index = lstNew.findIndex((x) => x.id == optionId);
    if (index !== -1) {
      lstNew.splice(index, 1);
    }

    if (lstNew.length == 0) {
      setHasMultipleVariants(false);
      const singleState = toSingleVariantState(productVariants?.length ? productVariants : savedVariant);
      setOptions(singleState.options);
      setProductVariants(singleState.variants);
      emitVariantState(singleState.options, singleState.variants);
      return;
    }

    setHasMultipleVariants(true);
    setOptions(lstNew);
    setVariantChangeByUser(true);
    emitVariantState(lstNew, productVariants);
  };

  const setAllVariantPrice = (price) => {
    const lst = productVariants.map((x) => ({ ...x, original_price: price }));
    setProductVariants(lst);
    emitVariantState(options, lst);
  };

  const filteredOptions = options.filter((opt) => opt.id !== 'default');
  const opt0 = filteredOptions[0];
  const opt1 = filteredOptions[1];

  const getOptionValue = (variant, optionId) => variant?.options?.find((o) => o.id === optionId)?.value || '';
  const getSelectedOrder = (opt, value) => {
    if (!opt?.values?.length) return Number.MAX_SAFE_INTEGER;
    const index = opt.values.findIndex((v) => v === value);
    return index === -1 ? Number.MAX_SAFE_INTEGER : index;
  };

  const sortedVariants = [...productVariants].sort((a, b) => {
    if (!opt0) return 0;
    const a0 = getOptionValue(a, opt0.id);
    const b0 = getOptionValue(b, opt0.id);
    const byOpt0 = getSelectedOrder(opt0, a0) - getSelectedOrder(opt0, b0);
    if (byOpt0 !== 0) return byOpt0;
    if (!opt1) return 0;
    const a1 = getOptionValue(a, opt1.id);
    const b1 = getOptionValue(b, opt1.id);
    const byOpt1 = getSelectedOrder(opt1, a1) - getSelectedOrder(opt1, b1);
    if (byOpt1 !== 0) return byOpt1;
    return String(a.variant_id || '').localeCompare(String(b.variant_id || ''));
  });

  return {
    id,
    form,
    product,
    options,
    productOptions,
    productVariants,
    hasMultipleVariants,
    sortedVariants,
    filteredOptions,
    navigate,
    onFinish,
    onFinishFailed,
    loadProductOptions,
    onMultipleVariantToggle,
    applyAttributeSelections,
    onSelectOptionChange,
    onUpdateProductVariant,
    onVariantImageChange,
    onGroupImageChange,
    onVariantOriginalPriceChange,
    attributeChange,
    removeOption,
    setAllVariantPrice,
  };
};

export default useComponent;

